import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Input, Tooltip, Spin, Avatar, Space, Popover, Card} from 'antd';
import { EditOutlined, DeleteOutlined, UserOutlined, BgColorsOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import { SketchPicker } from 'react-color';
import { useTranslation } from 'react-i18next';
import withRoleAccess from '../../hoc/withRoleAccess';
import PasswordModal from './PasswordModal';
import EditTeacherModal from './EditTeacherModal';
import { confirmAlert } from 'react-confirm-alert';
import TeacherNoteModal from './TeacherNoteModal';


function EditDeleteTeacher({ onCreateNewTeacher }) {
  const { t } = useTranslation();
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [teacherIdToDelete, setTeacherIdToDelete] = useState(null);
  const [teacherIdToEdit, setTeacherIdToEdit] = useState(null);
  const [passwordError, setPasswordError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [colorPickerVisible, setColorPickerVisible] = useState(null);
  const [selectedColor, setSelectedColor] = useState('#000000');
  const [noteModalOpen, setNoteModalOpen] = useState(false);
const [selectedTeacherId, setSelectedTeacherId] = useState(null);
  const itemsPerPage = 50;
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;

  const generateRandomColor = () => {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
      // Generate a random number between 0 and 7 for a darker shade
      color += letters[Math.floor(Math.random() * 8)];
    }
    return color;
  };
  
  // Generate an array of random dark colors
  const darkColors = Array.from({ length: 40 }, () => generateRandomColor());

    const fetchTeachers = useCallback(async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError(t('noToken'));
          setLoading(false);
          return;
        }
        const response = await axios.get(`${API_URL}/teachers`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
  
        // Sort teachers by userid before setting the state
        const sortedTeachers = response.data.sort((a, b) => a.userid - b.userid);
        setTeachers(sortedTeachers);  // Update the state with sorted teachers
      } catch (error) {
        console.error('Error fetching teachers:', error);
        setError(t('fetchTeachersError'));
      } finally {
        setLoading(false);
      }
    }, [API_URL, t]);
  
    useEffect(() => {
      fetchTeachers();
    }, [fetchTeachers]); // Now fetchTeachers is included in the dependency array
  
    // In EditDeleteTeacher.js
const saveTeacher = async (updatedTeacher) => {
    // The save already happened in the modal, just refresh the list
    confirmAlert({
        title: t('success'),
        message: t('teacherUpdated'),
        buttons: [{ label: 'OK', onClick: () => {} }]
    });

    await fetchTeachers();
    setIsEditModalOpen(false);
};
  
    const handleEdit = (teacherId) => {
      setTeacherIdToEdit(teacherId);
      setIsEditModalOpen(true);
    };
  
    const handleDelete = (teacherId) => {
      setTeacherIdToDelete(teacherId);
      setPasswordError(''); // Clear any previous error message
      setIsPasswordModalOpen(true);
    };
  
    const confirmDelete = async (password) => {
      setIsPasswordModalOpen(false);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError(t('noToken'));
          return;
        }
        await axios.delete(`${API_URL}/teachers/${teacherIdToDelete}`, {
          headers: {
            Authorization: `Bearer ${token}`
          },
          data: { password }
        });
        setTeachers(prev => prev.filter(teacher => teacher.userid !== teacherIdToDelete)); // Update local state
  
        confirmAlert({
          title: t('success'),
          message: t('teacherDeleted'),
          buttons: [
            {
              label: 'OK',
              onClick: () => {},
            }
          ]
        });
      } catch (error) {
        console.error('Error deleting teacher:', error);
        if (error.response && error.response.status === 401) {
          setPasswordError(t('invalidPassword'));
          setIsPasswordModalOpen(true); // Reopen the modal with an error message
        } else {
          setError(t('deleteTeacherError'));
        }
      }
    };
  
    const handleColorChange = (color) => {
      setSelectedColor(color.hex);
    };
  
    const handleSaveColor = async (teacherId) => {
      try {
        const token = localStorage.getItem('token');
        await axios.put(`${API_URL}/teachers/${teacherId}/color`, { color: selectedColor }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        confirmAlert({
          title: t('success'),
          message: t('colorUpdated'),
          buttons: [
            {
              label: 'OK',
              onClick: () => {},
            }
          ],
        });
        const updatedTeachers = teachers.map(teacher =>
          teacher.userid === teacherId ? { ...teacher, color: selectedColor } : teacher
        );
        setTeachers(updatedTeachers);
        setColorPickerVisible(null); // Close the color picker popup after saving
      } catch (error) {
        console.error('Error updating color:', error);
        confirmAlert({
          title: t('error'),
          message: t('errorUpdatingColor'),
          buttons: [
            {
              label: 'OK',
              onClick: () => {},
            }
          ],
        });
      }
    };
  
    // Filter and paginate teachers
    const filteredTeachers = teachers.filter(teacher =>
      teacher.firstname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      teacher.lastname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      teacher.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

  
    const columns = [
      { title: t('id'), dataIndex: 'userid', key: 'userid' },
      {
        title: '',
        key: 'dashboard',
        render: (_, teacher) => (
          <Tooltip title={t('viewDashboard')}>
            <Button
              shape="circle"
              icon={<UserOutlined />}
              onClick={() => navigate(`/teacher-dashboard-admin/${teacher.userid}`)}
            />
          </Tooltip>
        ),
      },
      {
        title: t('photo'),
        dataIndex: 'photourl',
        key: 'photo',
        render: (url, teacher) => (
          <Avatar src={url || 'default-path/defaultImg.jpg'} alt={`${teacher.firstname} ${teacher.lastname}`} />
        ),
      },
      { title: t('name'), dataIndex: 'firstname', key: 'name', render: (text, teacher) => `${teacher.firstname} ${teacher.lastname}` },
      {
        title: t('color'),
        key: 'color',
        render: (_, teacher) => (
          <div>
            <div
              style={{
                backgroundColor: teacher.color || '#ffffff',
                width: '30px',
                height: '30px',
                borderRadius: '4px',
                border: '1px solid #ddd',
                cursor: 'pointer',
              }}
              onClick={() => setColorPickerVisible(teacher.userid)}
            />
          </div>
        ),
      },
      { title: t('nickname'), dataIndex: 'nickname', key: 'nickname' },
      { title: t('subjects'), dataIndex: 'specialize_subjects', key: 'subjects' },
      {
        title: t('about'),
        dataIndex: 'schedule',
        key: 'about',
        render: (text) => (
          <div style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
            {text}
          </div>
        ),
      },
 {
      title: t('actions'),
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={t('edit')}>
            <Button 
              icon={<EditOutlined />} 
              size="small" 
              onClick={() => handleEdit(record.userid)}
            />
          </Tooltip>
          <Tooltip title={t('delete')}>
            <Button 
              danger 
              icon={<DeleteOutlined />} 
              size="small" 
              onClick={() => handleDelete(record.userid)}
            />
          </Tooltip>
          <Tooltip title={t('changeColor')}>
            <Popover
              content={
                <div>
                  <SketchPicker 
                    color={selectedColor} 
                    onChange={handleColorChange}
                    disableAlpha
                  />
                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    maxWidth: '220px',
                    marginTop: '10px'
                  }}>
                    {darkColors.map((color, index) => (
                      <div
                        key={index}
                        style={{ 
                          backgroundColor: color, 
                          width: '20px', 
                          height: '20px',
                          margin: '2px',
                          cursor: 'pointer',
                          border: '1px solid #ddd'
                        }}
                        onClick={() => setSelectedColor(color)}
                      />
                    ))}
                  </div>
                  <Button 
                    type="primary" 
                    icon={<SaveOutlined />} 
                    onClick={() => handleSaveColor(record.userid)}
                    style={{ marginTop: '10px', width: '100%' }}
                  >
                    {t('save')}
                  </Button>
                </div>
              }
              title={t('selectColor')}
              trigger="click"
              open={colorPickerVisible === record.userid}
              onOpenChange={(visible) => {
                if (visible) {
                  setColorPickerVisible(record.userid);
                  setSelectedColor(record.color || '#000000');
                } else {
                  setColorPickerVisible(null);
                }
              }}
            >
              <Button 
                icon={<BgColorsOutlined />} 
                size="small"
              />
            </Popover>
          </Tooltip>
        </Space>
      )
    },
      {
        title: t('Notes'),
        key: 'notes',
        render: (_, record) => (
          <Button
            size="small"
            icon={<span role="img" aria-label="note">📝</span>}
            onClick={() => {
              setSelectedTeacherId(record.userid); // or record.teacher_id
              setNoteModalOpen(true);
            }}
          >
            {t('Notes')}
          </Button>
        )
      }
    ];
  
  
    if (loading) return <Spin tip="Loading..." fullscreen />;
  
    return (
      <div>
        <div >
          <Button type="primary" onClick={onCreateNewTeacher}>
            <UserOutlined /> {t('createNewTeacher')}
          </Button>

           <Card style={{ marginBottom: '16px', marginTop: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
                    <Button onClick={fetchTeachers}>
  <ReloadOutlined />{t('Refresh')}
  </Button>
          <Input
            placeholder={t('searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
     
  </div>
           <div style={{ overflowX: 'auto', width: '100%' }}>
              <Table
                columns={columns}
                dataSource={filteredTeachers}
                rowKey="userid"
                scroll={{ x: 'max-content' }}
                style={{ overflowX: 'auto' }} 
                pagination={{
                  current: currentPage,
                  pageSize: itemsPerPage,
                  total: filteredTeachers.length,
                  onChange: setCurrentPage,
                }}
              />
            </div>
                    </Card>
        </div>

        <PasswordModal
          isOpen={isPasswordModalOpen}
          onClose={() => setIsPasswordModalOpen(false)}
          onConfirm={confirmDelete}
          errorMessage={passwordError}
        />
        <EditTeacherModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={saveTeacher}
          teacherId={teacherIdToEdit}
        />
        <TeacherNoteModal
          visible={noteModalOpen}
          onClose={() => setNoteModalOpen(false)}
          teacherId={selectedTeacherId}
        />
      </div>
      
    );
  } 
  
  export default withRoleAccess(EditDeleteTeacher, ['admin', 'superadmin']);